import { useCallback, useMemo, useRef, useState } from 'react';
import {
  Box, Card, CardBody, VStack, HStack, Text, Button, Textarea, Input,
  FormControl, FormLabel, IconButton
} from '@chakra-ui/react';
import { PageHeader } from '../../shell/PageHeader';
import { apiJson, isApiError } from '../../auth/apiFetch';
import { useBrand } from '../../brand/BrandContext';
import { slugHandle } from '../../components/adChrome';
import type { BrandSummary } from '../../brand/types';
import type { ShowcaseAd, ShowcaseCfg, ShowcaseProduct } from './types';
import { parseAdIdsFromText, parseAdIdsFromXlsx } from './parseAdIdList';
import { PickFromCampaign } from './PickFromCampaign';
import { ShowcaseView } from './ShowcaseView';

type Skipped = { id: string; reason: string };

type ByIdsResponse = {
  products: ShowcaseProduct[];
  ads: ShowcaseAd[];
  skipped: Skipped[];
};

type CreateResponse = { id: string; token: string };

export function seedCfgFromBrand(brand: BrandSummary): ShowcaseCfg {
  const handle = (brand.igHandle || '').replace(/^@/, '') || slugHandle(brand.name);
  return {
    brandName:    brand.name,
    handle,
    logoUrl:      brand.logoUrl,
    domain:       brand.websiteUrl,
    cta:          'Shop now',
    headline:     '',
    primary:      '',
    description:  '',
    showSponsored: true,
    showEngagement: true
  };
}

export function AdShowcasePage() {
  const { activeBrand } = useBrand();
  const fileRef = useRef<HTMLInputElement>(null);

  const [rawIds, setRawIds] = useState('');
  const [title, setTitle] = useState('');
  const [building, setBuilding] = useState(false);
  const [buildError, setBuildError] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [built, setBuilt] = useState(false);
  const [products, setProducts] = useState<ShowcaseProduct[]>([]);
  const [ads, setAds] = useState<ShowcaseAd[]>([]);
  const [skipped, setSkipped] = useState<Skipped[]>([]);
  const [skipDismissed, setSkipDismissed] = useState(false);

  const [cfg, setCfg] = useState<ShowcaseCfg | null>(null);
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [view, setView] = useState<'rows' | 'wall'>('rows');
  const [surround, setSurround] = useState<'dark' | 'light'>('dark');
  const [shareState, setShareState] = useState<{ sharing: boolean; url: string | null; error: string | null }>({
    sharing: false, url: null, error: null
  });

  const parsed = useMemo(() => parseAdIdsFromText(rawIds), [rawIds]);

  const onFile = useCallback(async (file: File) => {
    setFileError(null);
    try {
      const name = file.name.toLowerCase();
      let ids: string[];
      if (name.endsWith('.xlsx') || name.endsWith('.xls')) {
        const result = await parseAdIdsFromXlsx(file);
        ids = result.ids;
      } else {
        const text = await file.text();
        ids = parseAdIdsFromText(text).ids;
      }
      setRawIds(ids.join('\n'));
    } catch (e) {
      setFileError(e instanceof Error ? e.message : 'Could not read that file');
    }
  }, []);

  const build = useCallback(async () => {
    if (!activeBrand || parsed.ids.length === 0) return;
    setBuilding(true);
    setBuildError(null);
    try {
      const data = await apiJson<ByIdsResponse>('/api/ads/by-ids', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brandId: activeBrand.id, adIds: parsed.ids })
      });
      const seeded = seedCfgFromBrand(activeBrand);
      setCfg(seeded);
      setProducts(data.products || []);
      setAds(data.ads || []);
      setSkipped(data.skipped || []);
      setSkipDismissed(false);
      setOverrides({});
      setView('rows');
      setSurround('dark');
      setShareState({ sharing: false, url: null, error: null });
      if (!title.trim()) setTitle(`${activeBrand.name} showcase`);
      setBuilt(true);
    } catch (e) {
      setBuildError(e instanceof Error ? e.message : String(e));
    } finally {
      setBuilding(false);
    }
  }, [activeBrand, parsed.ids, title]);

  const share = useCallback(async () => {
    if (!activeBrand || !cfg) return;
    setShareState(s => ({ ...s, sharing: true, error: null }));
    try {
      const data = await apiJson<CreateResponse>('/api/ad-showcases', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          brandId: activeBrand.id,
          title: title.trim() || `${activeBrand.name} showcase`,
          adIds: ads.map(a => a.id),
          cfg,
          overrides,
          view,
          surround
        })
      });
      const url = `${window.location.origin}/showcase/${data.token}`;
      void navigator.clipboard.writeText(url).catch(() => { /* ShowcaseView also copies */ });
      setShareState({ sharing: false, url, error: null });
    } catch (e) {
      let message = e instanceof Error ? e.message : String(e);
      if (isApiError(e) && e.status === 422) {
        const body = e.body as { error?: string; skipped?: Skipped[] } | null;
        const extra = body?.skipped?.length
          ? ` (${body.skipped.length} id${body.skipped.length === 1 ? '' : 's'} skipped)`
          : '';
        message = (body?.error || message) + extra;
      }
      setShareState({ sharing: false, url: null, error: message });
    }
  }, [activeBrand, cfg, title, ads, overrides, view, surround]);

  if (!activeBrand) {
    return (
      <VStack align="stretch" spacing={4}>
        <PageHeader title="Ad Showcase" description="Preview a set of ads in real platform chrome, then share a snapshot with a client." />
        <Card variant="outline"><CardBody><Text fontSize="sm" color="brand.muted">Select a brand to continue.</Text></CardBody></Card>
      </VStack>
    );
  }

  if (built && cfg) {
    return (
      <VStack align="stretch" spacing={3}>
        {!skipDismissed && skipped.length > 0 && (
          <HStack
            bg="orange.50"
            borderWidth="1px"
            borderColor="orange.200"
            borderRadius="md"
            px={4}
            py={2}
            spacing={3}
          >
            <Text fontSize="sm" color="brand.ink" flex={1}>
              {skipped.length} id{skipped.length === 1 ? '' : 's'} could not be included
              {' '}({skipped.slice(0, 3).map(s => s.reason).join('; ')}
              {skipped.length > 3 ? '…' : ''})
            </Text>
            <IconButton
              aria-label="Dismiss"
              size="xs"
              variant="ghost"
              onClick={() => setSkipDismissed(true)}
              icon={<Box as="span">×</Box>}
            />
          </HStack>
        )}
        <ShowcaseView
          title={title}
          cfg={cfg}
          overrides={overrides}
          view={view}
          surround={surround}
          products={products}
          ads={ads}
          isSnapshot={false}
          canConfigure
          onTitleChange={setTitle}
          onCfgChange={patch => setCfg(c => c ? { ...c, ...patch } : c)}
          onOverrideChange={(adId, kind) => setOverrides(o => ({ ...o, [adId]: kind }))}
          onViewChange={setView}
          onSurroundChange={setSurround}
          onShare={share}
          shareState={shareState}
        />
      </VStack>
    );
  }

  const countLabel = parsed.ids.length === 0
    ? 'Paste or upload Ad IDs to begin'
    : `${parsed.ids.length} ad ID${parsed.ids.length === 1 ? '' : 's'} found${parsed.invalidCount ? `, ${parsed.invalidCount} line${parsed.invalidCount === 1 ? '' : 's'} ignored` : ''}`;

  return (
    <VStack align="stretch" spacing={6}>
      <PageHeader
        title="Ad Showcase"
        description="Pick ads from a campaign or paste Ad IDs, preview them in real platform chrome, and share a snapshot URL with a client."
      />
      <Card>
        <CardBody>
          <VStack align="stretch" spacing={5}>
            <FormControl>
              <FormLabel fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                Title
              </FormLabel>
              <Input
                value={title}
                onChange={e => setTitle(e.target.value)}
                placeholder={`${activeBrand.name} showcase`}
              />
            </FormControl>

            <PickFromCampaign
              brandId={activeBrand.id}
              onFill={ids => setRawIds(ids.join('\n'))}
            />

            <FormControl>
              <FormLabel fontSize="11px" fontWeight="700" textTransform="uppercase" letterSpacing="0.06em" color="brand.muted">
                Ad IDs
              </FormLabel>
              <Textarea
                value={rawIds}
                onChange={e => setRawIds(e.target.value)}
                placeholder="Paste Mongo ObjectIds — one per line, or comma / whitespace separated"
                minH="160px"
                fontFamily="mono"
                fontSize="13px"
              />
            </FormControl>

            <HStack spacing={3} flexWrap="wrap">
              <input
                ref={fileRef}
                type="file"
                accept=".txt,.csv,.xlsx"
                hidden
                onChange={e => {
                  const f = e.target.files?.[0];
                  e.target.value = '';
                  if (f) void onFile(f);
                }}
              />
              <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()}>
                Upload .txt / .csv / .xlsx
              </Button>
              <Text fontSize="sm" color="brand.muted">{countLabel}</Text>
            </HStack>
            {fileError && <Text fontSize="sm" color="red.600">{fileError}</Text>}
            {buildError && <Text fontSize="sm" color="red.600">{buildError}</Text>}

            <HStack justify="flex-end">
              <Button
                variant="brand"
                onClick={() => void build()}
                isDisabled={parsed.ids.length === 0 || building}
                isLoading={building}
                loadingText="Building…"
              >
                Build showcase
              </Button>
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}

export default AdShowcasePage;
